import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import {
  buildPickOneEntryHref,
  buildPickOneStarterHref,
} from "@/hooks/use-pick-one-templates";
import { apiFetch } from "@/lib/api-client";

const ALLOWED_ONBOARDING_TEMPLATES = new Set([
  "dental",
  "fitness",
  "salon",
  "legal",
  "real_estate",
  "plumbing",
  "hvac",
  "landscaping",
  "restaurant",
  "accounting",
  "pet_grooming",
  "spa",
  "tutoring",
]);

type TemplateApplyResponse = {
  applied?: boolean;
  industry?: string;
  created?: {
    pipelineStages?: number;
    workflows?: number;
    pages?: number;
    emailTemplates?: number;
    bookingServices?: number;
    communitySpaces?: number;
  };
};

function normalizeTemplateKey(value: string): string {
  return value.trim().toLowerCase().replace(/-/g, "_");
}

function titleCaseTemplate(value: string): string {
  return value
    .replace(/[-_]/g, " ")
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export default function TemplateBrowser() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const template = searchParams.get("template");
  const starter = searchParams.get("starter") ?? undefined;
  const siteId = searchParams.get("siteId");
  const [applyError, setApplyError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!template) {
      return;
    }

    const templateKey = normalizeTemplateKey(template);
    let cancelled = false;

    if (!ALLOWED_ONBOARDING_TEMPLATES.has(templateKey)) {
      setApplyError("Unsupported onboarding template");
      return;
    }

    async function applyTemplate() {
      setApplyError(null);
      try {
        const applyResult = await apiFetch<TemplateApplyResponse>(`/api/onboarding/templates/${encodeURIComponent(templateKey)}/apply`, {
          method: "POST",
          body: JSON.stringify({}),
        });

        if (!cancelled) {
          if (applyResult.created) {
            window.sessionStorage.setItem("ease:onboarding:last-template-apply", JSON.stringify({
              template: templateKey,
              industry: applyResult.industry ?? templateKey,
              created: applyResult.created,
              appliedAt: new Date().toISOString(),
            }));
          }

          navigate(buildPickOneStarterHref(templateKey, {
            starterPageId: starter,
            siteId,
            source: "onboarding",
          }), { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          setApplyError(error instanceof Error ? error.message : "Failed to apply onboarding template");
        }
      }
    }

    void applyTemplate();

    return () => {
      cancelled = true;
    };
  }, [attempt, navigate, siteId, starter, template]);

  if (template) {
    return (
      <div className="mx-auto max-w-xl rounded-xl border border-zinc-200 bg-background p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
        <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Applying your onboarding template</h1>
        <p className="mt-2 text-sm text-zinc-500">
          We are creating the CRM pipeline, workflows, email templates, booking services, and starter page workspace for this vertical.
        </p>
        {applyError ? (
          <div
            role="alert"
            aria-live="assertive"
            className="mt-4 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4"
          >
            <p className="text-sm text-[var(--color-destructive)]">{applyError}</p>
            {applyError !== "Unsupported onboarding template" && (
              <button
                type="button"
                onClick={() => setAttempt((value) => value + 1)}
                className="mt-3 rounded-md bg-[var(--color-destructive)]/10 px-3 py-2 text-sm font-medium text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/20"
              >
                Retry template application
              </button>
            )}
          </div>
        ) : (
          <div
            className="mt-4 flex items-center gap-3 text-sm text-zinc-600 dark:text-zinc-300"
            aria-busy="true"
            aria-live="polite"
          >
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-200 border-t-[var(--color-primary)]"
              aria-hidden="true"
            />
            <span>Applying {titleCaseTemplate(template)}...</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <Navigate
      replace
      to={buildPickOneEntryHref({ siteId, source: "onboarding" })}
    />
  );
}
